import React from 'react';
import {
  DocumentTextIcon,
  UserIcon,
  CalendarIcon,
  MapPinIcon,
  CheckCircleIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
  LightBulbIcon,
  ClipboardDocumentListIcon,
  ExclamationTriangleIcon,
  ScaleIcon,
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  PencilIcon,
  EyeIcon,
  BriefcaseIcon,
  DocumentArrowDownIcon,

} from '@heroicons/react/24/outline';

export default function ReportingHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-violet-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-purple-500 to-violet-600 rounded-full mb-6">
            <DocumentTextIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Reporting & Assessment Guide</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Professional staff assessment and reporting system for comprehensive performance management
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">What is the Reporting System?</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                This is your comprehensive staff assessment and reporting platform - a professional tool designed to streamline performance management, disciplinary procedures, and development tracking. Create structured assessments, document important conversations, and maintain detailed records of staff progress and organizational compliance all within one unified, user-friendly interface.
              </p>
            </div>
          </div>
        </div>

        {/* Assessment Types Overview */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Assessment Types & Their Purpose</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Annual Appraisal */}
            <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ClipboardDocumentListIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Annual Appraisal</h3>
              <p className="text-sm text-gray-600">Comprehensive yearly performance review and goal setting</p>
            </div>

            {/* Probation Review */}
            <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
              <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ScaleIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Probation Review</h3>
              <p className="text-sm text-gray-600">Assessment of new staff during trial period</p>
            </div>

            {/* Monthly Check-in */}
            <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <CalendarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Monthly Check-in</h3>
              <p className="text-sm text-gray-600">Regular progress monitoring and feedback sessions</p>
            </div>

            {/* Performance Review */}
            <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ChartBarIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Performance Review</h3>
              <p className="text-sm text-gray-600">Focused assessment of specific performance areas</p>
            </div>

            {/* Disciplinary Action */}
            <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ExclamationTriangleIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Disciplinary Action</h3>
              <p className="text-sm text-gray-600">Formal documentation of policy violations and corrective actions</p>
            </div>

            {/* Meeting Formats */}
            <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-200">
              <div className="w-12 h-12 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <MapPinIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Meeting Formats</h3>
              <p className="text-sm text-gray-600">Face-to-face, virtual, or hybrid assessment meetings</p>
            </div>
          </div>
        </div>

        {/* Setup Process */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Assessment Creation Process</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Step 1 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">1</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select Staff Member</h3>
              <p className="text-gray-600 text-sm">Choose the employee for assessment from the dropdown list</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-purple-400" />
            </div>

            {/* Step 2 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">2</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Choose Assessment Type</h3>
              <p className="text-gray-600 text-sm">Select the appropriate assessment format and purpose</p>
            </div>
          </div>

          {/* Mobile flow indicator */}
          <div className="md:hidden flex justify-center items-center space-x-4 mt-6">
            <span className="text-purple-600 font-medium">1 → 2</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
            {/* Step 3 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">3</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Set Meeting Format</h3>
              <p className="text-gray-600 text-sm">Choose face-to-face, virtual, or hybrid meeting format</p>
            </div>

            {/* Arrow */}
            <div className="hidden md:flex items-center justify-center">
              <ArrowRightIcon className="w-8 h-8 text-purple-400" />
            </div>

            {/* Step 4 */}
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-purple-600">4</span>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Complete Assessment</h3>
              <p className="text-gray-600 text-sm">Fill out the structured form and generate professional documentation</p>
            </div>
          </div>

          {/* Mobile flow indicator */}
          <div className="md:hidden flex justify-center items-center space-x-4 mt-6">
            <span className="text-purple-600 font-medium">3 → 4</span>
          </div>
        </div>

        {/* Assessment Types Deep Dive */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Understanding Assessment Types</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Positive Assessments */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Development & Growth Assessments</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-blue-800 text-sm"><strong>Annual Appraisals:</strong> Comprehensive performance reviews with goal setting and career development planning</p>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded p-3">
                  <p className="text-orange-800 text-sm"><strong>Probation Reviews:</strong> Structured assessment of new employees during their trial period</p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-green-800 text-sm"><strong>Monthly Check-ins:</strong> Regular feedback sessions to monitor progress and provide ongoing support</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded p-3">
                  <p className="text-purple-800 text-sm"><strong>Performance Reviews:</strong> Focused assessments addressing specific performance areas or improvement needs</p>
                </div>
              </div>
            </div>

            {/* Disciplinary Assessments */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Disciplinary & Corrective Assessments</h3>
              </div>
              <div className="space-y-3">
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="text-red-800 text-sm"><strong>Disciplinary Actions:</strong> Formal documentation of policy violations, performance issues, or behavioral concerns</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="text-amber-800 text-sm"><strong>Corrective Action Plans:</strong> Structured improvement plans with clear expectations and timelines</p>
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <p className="text-gray-800 text-sm"><strong>Progressive Discipline:</strong> Escalating levels of disciplinary action based on severity and recurrence</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Meeting Formats & Best Practices */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Meeting Formats & Best Practices</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-blue-50 rounded-lg border border-blue-200">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <UserGroupIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Face-to-Face Meetings</h3>
              <p className="text-sm text-gray-600">Best for complex discussions, disciplinary matters, and building personal connections</p>
            </div>

            <div className="text-center p-6 bg-green-50 rounded-lg border border-green-200">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <ComputerDesktopIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Virtual Meetings</h3>
              <p className="text-sm text-gray-600">Convenient for remote staff, routine check-ins, and when travel isn't practical</p>
            </div>

            <div className="text-center p-6 bg-purple-50 rounded-lg border border-purple-200">
              <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-3">
                <EyeIcon className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Hybrid Meetings</h3>
              <p className="text-sm text-gray-600">Combines in-person and remote participation for flexible team arrangements</p>
            </div>
          </div>
        </div>

        {/* Interface Features */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Interface Features & Navigation</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Progress Tracking */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Setup Progress Indicator</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Visual progress bar showing completion status of the three setup steps: staff selection, assessment type, and meeting format.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded p-3">
                <p className="text-purple-800 text-sm">Assessment forms only become available once all setup steps are completed</p>
              </div>
            </div>

            {/* Staff Details Panel */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Collapsible Staff Panel</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                View comprehensive staff information alongside the assessment form. Can be minimized for full-screen form completion.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-blue-800 text-sm">Floating button appears when panel is minimized for quick access to staff details</p>
              </div>
            </div>

            {/* Mobile Responsiveness */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <DevicePhoneMobileIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Mobile Optimization</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Fully responsive design that adapts to mobile devices with single-column layout and touch-friendly controls.
              </p>
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-green-800 text-sm">Floating staff details button repositions automatically for mobile screens</p>
              </div>
            </div>

            {/* Form Validation */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <CheckCircleIcon className="w-5 h-5 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Smart Form Generation</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Different assessment types generate appropriate forms automatically - appraisals, disciplinary actions, probation reviews, etc.
              </p>
              <div className="bg-indigo-50 border border-indigo-200 rounded p-3">
                <p className="text-indigo-800 text-sm">Each form type is specifically designed for its purpose with relevant fields and sections</p>
              </div>
            </div>
          </div>
        </div>

        {/* Resignation Form */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 text-center">Resignation Form Guide</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center">
                  <BriefcaseIcon className="w-5 h-5 text-rose-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">What it does</h3>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                The resignation form captures leaving details, stores them against the staff profile, and generates a professional resignation acknowledgement letter for HR records.
              </p>
              <div className="bg-rose-50 border border-rose-200 rounded p-3">
                <p className="text-rose-800 text-sm">Use this when an employee submits formal notice so records and documentation stay consistent.</p>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <PencilIcon className="w-5 h-5 text-indigo-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Required inputs</h3>
              </div>
              <ul className="space-y-2 text-sm text-gray-700">
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 mt-0.5 text-green-600" /><span>Leaving reason</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 mt-0.5 text-green-600" /><span>Leaving date</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 mt-0.5 text-green-600" /><span>Staff name and role (auto-populated)</span></li>
                <li className="flex items-start gap-2"><CheckCircleIcon className="w-4 h-4 mt-0.5 text-green-600" /><span>Notice period (auto-loaded from company policy)</span></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">1</span>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Complete details</h4>
              <p className="text-xs text-gray-600">Enter leaving reason and date in the resignation form.</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white font-bold">2</span>
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Generate letter</h4>
              <p className="text-xs text-gray-600">Submit to generate a resignation acknowledgement document.</p>
            </div>

            <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="w-12 h-12 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-3">
                <DocumentArrowDownIcon className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Open or download</h4>
              <p className="text-xs text-gray-600">The letter opens in a new tab as PDF/HTML or downloads automatically.</p>
            </div>
          </div>
        </div>

        {/* Tips Section */}
        <div className="bg-gradient-to-r from-purple-50 to-violet-50 rounded-2xl p-6 md:p-8 border border-purple-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Assessment Best Practices</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Prepare thoroughly by reviewing staff performance data before meetings</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Choose the appropriate assessment type based on the purpose and staff situation</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use face-to-face meetings for complex or sensitive discussions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Document all agreements, goals, and action items clearly in the assessment</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Schedule regular check-ins to monitor progress on development goals</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">For disciplinary matters, follow proper procedures and document everything thoroughly</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Use probation reviews to provide clear feedback and set expectations for new staff</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Keep assessment records confidential and secure as required by data protection regulations</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Balance positive feedback with constructive criticism to motivate staff development</p>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <p className="text-gray-700 text-sm">Set SMART goals (Specific, Measurable, Achievable, Relevant, Time-bound) in appraisals</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}